import azureImg from '@/assets/fish/azure.png'
import amberImg from '@/assets/fish/amber.png'
import novaImg from '@/assets/fish/nova.png'
import onyxImg from '@/assets/fish/onyx.png'
import coralImg from '@/assets/fish/coral.png'
import clownImg from '@/assets/fish/clown.png'
//import silverImg from '@/assets/fish/silver.png'

export interface FishStats {
  satiety: number // 饱食度
  mood: number // 心情值
  bond: number // 亲密度
  energy: number // 活力值
  growthStage: number // 成长阶段 (0-4)
  totalExperience: number // 累计获得的经验值
}

export interface FishGrowthConfig {
  stages: number[]
  scales: number[]
  weightModifiers: {
    feeding: number
    speed: number
    stats: number
  }
}

export const FISH_GROWTH_CONFIG: FishGrowthConfig = {
  stages: [5, 10, 15, 20],
  scales: [0.8, 0.95, 1.1, 1.25, 1.4],
  weightModifiers: {
    feeding: 0.15,
    speed: 0.1,
    stats: 0.2,
  },
}

export interface FishDef {
  id: string
  code: string
  name: string
  species: string
  trait: string
  traitColor: string
  quote: string
  desc: string
  img: string
  difficulty: '简单' | '中等' | '困难'
  base: FishStats
  depthRange: [number, number]
  swimSpeed: number
  rarity: 'common' | 'rare' | 'epic' | 'legendary'
  hidden?: boolean
  unlockCondition?: {
    type: 'level' | 'bond' | 'clean'
    value: number
    label: string
  }
}

export const getGrowthStage = (level: number): number => {
  const { stages } = FISH_GROWTH_CONFIG
  for (let i = stages.length - 1; i >= 0; i--) {
    if (level >= stages[i]) return i + 1
  }
  return 0
}

export const getGrowthScale = (stage: number): number => {
  const { scales } = FISH_GROWTH_CONFIG
  return scales[Math.min(stage, scales.length - 1)]
}

export const getGrowthStageName = (stage: number): string => {
  const names = ['幼年', '成长', '青年', '成熟', '完美']
  return names[Math.min(stage, names.length - 1)]
}

export const FISHES: FishDef[] = [
  {
    id: 'azure',
    code: 'AZURE-07',
    name: '蓝焰',
    species: '暹罗斗鱼',
    trait: '高傲',
    traitColor: '#3b82f6',
    quote: '“一缸之主，不容侵犯。”',
    desc: '蓝焰有着火焰般张扬的尾鳍，领地意识极强。它只回应有耐心的主人，但一旦认可你，会主动游到指尖前展示华丽的鳍。',
    img: azureImg,
    difficulty: '中等',
    base: { satiety: 720, mood: 680, bond: 420, energy: 880, growthStage: 0, totalExperience: 0 },
    depthRange: [20, 50],
    swimSpeed: 1.2,
    rarity: 'common',
  },
  {
    id: 'amber',
    code: 'AMBER-12',
    name: '琉金',
    species: '兰寿金鱼',
    trait: '憨厚',
    traitColor: '#f59e0b',
    quote: '“圆滚滚的幸福，就是吃饱晒太阳。”',
    desc: '琉金是缸里的开心果，永远慢悠悠地晃着圆肚子。它对食物毫无抵抗力，闻到饲料味会第一个冲到水面。',
    img: amberImg,
    difficulty: '简单',
    base: { satiety: 560, mood: 820, bond: 610, energy: 640, growthStage: 0, totalExperience: 0 },
    depthRange: [60, 85],
    swimSpeed: 0.6,
    rarity: 'common',
  },
  {
    id: 'nova',
    code: 'NOVA-63',
    name: '新星',
    species: '孔雀鱼',
    trait: '活泼',
    traitColor: '#10b981',
    quote: '“尾巴一摆，全场最闪。”',
    desc: '新星拥有一把霓虹色的大尾巴，是天生的表演家。它最喜欢追着光斑和指尖游动，越逗越兴奋。',
    img: novaImg,
    difficulty: '简单',
    base: { satiety: 660, mood: 900, bond: 540, energy: 950, growthStage: 0, totalExperience: 0 },
    depthRange: [25, 55],
    swimSpeed: 1.5,
    rarity: 'common',
  },
  {
    id: 'onyx',
    code: 'ONYX-04',
    name: '墨瞳',
    species: '龙睛金鱼',
    trait: '神秘',
    traitColor: '#8b5cf6',
    quote: '“深渊般的眼睛里，藏着星图。”',
    desc: '墨瞳总是静静地悬在角落观察一切。它不易亲近，但深夜的灯光下，它会悄悄游到你面前吐一串泡泡。',
    img: onyxImg,
    difficulty: '困难',
    base: { satiety: 700, mood: 560, bond: 300, energy: 620, growthStage: 0, totalExperience: 0 },
    depthRange: [40, 70],
    swimSpeed: 0.8,
    rarity: 'rare',
  },
  {
    id: 'coral',
    code: 'CORAL-21',
    name: '珊瑚',
    species: '红白锦鲤',
    trait: '优雅',
    traitColor: '#ef4444',
    quote: '“缓缓游过的地方，好运随之而来。”',
    desc: '珊瑚是缸中气质最出众的一条，红白相间的花纹如同流动的缎带。据说悉心照料它的人，会收获一整天的好运。',
    img: coralImg,
    difficulty: '中等',
    base: { satiety: 780, mood: 740, bond: 500, energy: 760, growthStage: 0, totalExperience: 0 },
    depthRange: [35, 65],
    swimSpeed: 1.0,
    rarity: 'rare',
  },
  {
    id: 'clown',
    code: 'CLOWN-77',
    name: '小丑鱼',
    species: '海葵鱼',
    trait: '活泼',
    traitColor: '#f97316',
    quote: '“海葵是我家，快乐闯天下。”',
    desc: '小丑鱼有着鲜艳的橙色身体和白色条纹，性格活泼好动，喜欢在海葵附近游动。它是海洋中的开心果，能给鱼缸带来欢乐的氛围。',
    img: clownImg,
    difficulty: '简单',
    base: { satiety: 600, mood: 920, bond: 650, energy: 900, growthStage: 0, totalExperience: 0 },
    depthRange: [20, 50],
    swimSpeed: 1.4,
    rarity: 'common',
    hidden: true,
  },
  {
    id: 'silver',
    code: 'SILVER-09',
    name: '银鳞',
    species: '银龙鱼',
    trait: '威武',
    traitColor: '#94a3b8',
    quote: '“水中之王，傲视群雄。”',
    desc: '银鳞有着银色的鳞片和长长的触须，是真正的水中王者。它性格高傲，只有真正有经验的馆长才能驯服它。',
    img: azureImg,
    difficulty: '困难',
    base: { satiety: 800, mood: 600, bond: 200, energy: 900, growthStage: 0, totalExperience: 0 },
    depthRange: [15, 45],
    swimSpeed: 1.3,
    rarity: 'epic',
    hidden: true,
    unlockCondition: { type: 'level', value: 15, label: '馆长等级达到 15' },
  },
  {
    id: 'jade',
    code: 'JADE-47',
    name: '翡翠',
    species: '霓虹脂鲤',
    trait: '灵动',
    traitColor: '#22c55e',
    quote: '“像翡翠一样闪耀，像精灵一样轻盈。”',
    desc: '翡翠是一条闪闪发光的小鱼，身体散发着迷人的绿色光芒。它非常活泼，喜欢在水草间穿梭嬉戏。',
    img: novaImg,
    difficulty: '简单',
    base: { satiety: 580, mood: 950, bond: 700, energy: 980, growthStage: 0, totalExperience: 0 },
    depthRange: [30, 60],
    swimSpeed: 1.8,
    rarity: 'epic',
    hidden: true,
    unlockCondition: { type: 'bond', value: 2000, label: '亲密度总和达到 2000' },
  },
  {
    id: 'purple',
    code: 'PURPLE-33',
    name: '紫霞',
    species: '紫神仙鱼',
    trait: '高贵',
    traitColor: '#a855f7',
    quote: '“紫色的霞光，映照高贵的灵魂。”',
    desc: '紫霞拥有优雅的紫色身体和飘逸的长鳍，如同水中的仙子。它性情温和，但只有细心照料才能赢得它的信任。',
    img: onyxImg,
    difficulty: '中等',
    base: { satiety: 750, mood: 700, bond: 350, energy: 780, growthStage: 0, totalExperience: 0 },
    depthRange: [25, 55],
    swimSpeed: 0.9,
    rarity: 'legendary',
    hidden: true,
    unlockCondition: { type: 'clean', value: 50, label: '累计清洁 50 次' },
  },
]

export const TOTAL_COLLECTION = 9 // 图鉴总数(含未解锁)

export const VISIBLE_FISHES = FISHES.filter(f => !f.hidden)

export const clamp = (v: number, min = 0, max = 1000) => Math.min(max, Math.max(min, v))
